import db from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";

// Función auxiliar para normalizar nombres de propiedades
const normalizeFieldNames = (bcLine) => {
    // Verificar cada propiedad para manejar diferentes formatos de nombres
    const normalized = {};

    // Normalizar propiedades principales
    normalized.No = bcLine.No || "";
    normalized.Parent_Item_No = bcLine["Parent_Item_No"] || bcLine["Parent Item No."] || "";

    // Detectar y normalizar Line_No (podría venir como "Line_No" o "Line No.")
    if (bcLine["Line_No"] !== undefined) {
        normalized.Line_No = bcLine["Line_No"];
    } else if (bcLine["Line No."] !== undefined) {
        normalized.Line_No = bcLine["Line No."];
    } else {
        normalized.Line_No = 0;
    }

    // Normalizar otros campos
    normalized.Type = bcLine.Type || "";
    normalized.Description = bcLine.Description || "";
    normalized.Source_Counter = bcLine.Source_Counter || 0;

    // Detectar y normalizar Quantity_per
    if (bcLine["Quantity_per"] !== undefined) {
        normalized.Quantity_per = bcLine["Quantity_per"];
    } else if (bcLine["Quantity per"] !== undefined) {
        normalized.Quantity_per = bcLine["Quantity per"];
    } else {
        normalized.Quantity_per = 0;
    }

    // Detectar y normalizar Unit_of_Measure_Code
    if (bcLine["Unit_of_Measure_Code"] !== undefined) {
        normalized.Unit_of_Measure_Code = bcLine["Unit_of_Measure_Code"];
    } else if (bcLine["Unit of Measure Code"] !== undefined) {
        normalized.Unit_of_Measure_Code = bcLine["Unit of Measure Code"];
    } else {
        normalized.Unit_of_Measure_Code = "";
    }

    // Normalizar Variant_Code
    if (bcLine["Variant_Code"] !== undefined) {
        normalized.Variant_Code = bcLine["Variant_Code"];
    } else if (bcLine["Variant Code"] !== undefined) {
        normalized.Variant_Code = bcLine["Variant Code"];
    } else {
        normalized.Variant_Code = "";
    }
    return normalized;
};

// Obtener todas las líneas para un producto padre específico
export const getProductLines = async (parentItemNo) => {
    try {
        const productLines = await db.productLines
            .where("Parent_Item_No")
            .equals(parentItemNo)
            .and(line => !line.deleted)
            .toArray();

        return productLines;
    } catch (error) {
        console.error(`Error al obtener líneas para el producto ${parentItemNo}:`, error);
        throw error;
    }
};

// Sincronizar líneas de producto desde Business Central
export const syncProductLines = async () => {
    if (!isOnline()) {
        return false;
    }

    try {
        const localProductsLines = await db.productLines.toArray();

        let lastSourceCode = null;

        if (localProductsLines.length > 0) {
            lastSourceCode = localProductsLines.sort((a, b) => a.Source_Counter - b.Source_Counter)[localProductsLines.length - 1].Source_Counter;
        }

        console.log("lastSourceCode", lastSourceCode);

        const response = await fetchFromBusinessCentral("Kits" + (lastSourceCode ? `?$filter=Source_Counter gt ${lastSourceCode}` : ""));

        // Verificar que la respuesta sea un array
        if (!response || !Array.isArray(response)) {
            console.error("La respuesta de Business Central no es un array:", response);
            return false;
        }

        const bcProductLines = response;

        if (bcProductLines.length === 0) {
            console.log("No se encontraron nuevas líneas de producto en el servidor");
            return true;
        }

        // Crear mapa de líneas locales para búsqueda rápida
        const localLinesByKey = {};
        localProductsLines.forEach(line => {
            const key = `${line.Parent_Item_No}-${line.Line_No}-${line.No}`;
            localLinesByKey[key] = line;
        });

        // Actualizar líneas existentes y añadir nuevas desde el servidor
        for (const bcLine of bcProductLines) {
            try {
                // Normalizar nombres de propiedades
                const normalizedLine = normalizeFieldNames(bcLine);

                // Verificar que los campos obligatorios existan
                if (!normalizedLine.No || !normalizedLine.Parent_Item_No) {
                    continue;
                }

                const key = `${normalizedLine.Parent_Item_No}-${normalizedLine.Line_No}-${normalizedLine.No}`;
                const existingLine = localLinesByKey[key];

                if (existingLine) {
                    // Actualizar línea existente si no está pendiente de sincronización
                    if (existingLine.pendingSync === 0) {
                        await db.productLines.update(existingLine.id, {
                            ...normalizedLine,
                            lastModified: new Date(),
                            lastSynced: new Date(),
                            pendingSync: 0,
                            deleted: false
                        });
                    }
                } else {
                    // Añadir nueva línea del servidor si no existe localmente
                    await db.productLines.add({
                        ...normalizedLine,
                        lastSynced: new Date(),
                        pendingSync: 0,
                        deleted: false
                    });
                }
            } catch (lineError) {
                console.error("Error al procesar línea:", lineError, bcLine);
            }
        }

        console.log(`Sincronización de líneas de producto completada: ${bcProductLines.length} líneas procesadas`);
        return true;
    } catch (error) {
        console.error("Error al sincronizar líneas de producto:", error);
        return false;
    }
};

// Verificar si un producto tiene líneas asociadas
export const hasProductLines = async (productNo) => {
    try {
        const count = await db.productLines
            .where("Parent_Item_No")
            .equals(productNo)
            .and(line => !line.deleted)
            .count();

        return count > 0;
    } catch (error) {
        console.error(`Error al verificar líneas para el producto ${productNo}:`, error);
        return false;
    }
}; 